import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, AlertOctagon, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { fmtMinutes } from "../utils/pricing";

/**
 * FidelityBanner — Phase 5.0.
 *
 * Sits above the module view in StoryboardEditor and VideoStudio.
 * Renders one of three states based on the platform's fidelity-audit:
 *
 *   1. RED banner   — one or more scenes drifted (SCRIPT_DRIFT). Tells
 *      the reviewer to re-approve the affected scenes; no retry button
 *      because the script itself is the problem.
 *   2. YELLOW banner — one or more scenes shipped with a substitute
 *      visual (IMAGE_FALLBACK). Lists the affected scene numbers and
 *      exposes a per-scene "Retry with approved image" button which
 *      re-enqueues the motion render (existing endpoint).
 *   3. GREEN pill    — every scene's approved script + visual is intact
 *      and A/V sync is within tolerance. Small pill, not a full banner.
 *
 * Auto-refreshes on prop change and after each retry click. Fully
 * white-labelled — never mentions a model name.
 */
export default function FidelityBanner({ moduleId, canRetry = true, onRetried, currentDurationS }) {
    const [audit, setAudit] = useState(null);
    const [loading, setLoading] = useState(false);
    const [retrying, setRetrying] = useState({}); // scene_id -> bool

    const refresh = useCallback(async () => {
        if (!moduleId) return;
        setLoading(true);
        try {
            const r = await api.get("/admin/debug/fidelity-audit", { params: { module_id: moduleId } });
            setAudit(r.data);
        } catch (err) {
            // 403 for non-admin roles is normal — hide silently.
            const code = err?.response?.status;
            if (code !== 403 && code !== 401 && code !== 404) {
                // Only toast on unexpected errors.
                console.warn("fidelity-audit fetch failed", err);
            }
            setAudit(null);
        } finally {
            setLoading(false);
        }
    }, [moduleId]);

    useEffect(() => { refresh(); }, [refresh]);

    const retryScene = useCallback(async (scene_id) => {
        setRetrying((s) => ({ ...s, [scene_id]: true }));
        try {
            // Works for EVERY scene type: remaps to the best document image and clears the
            // flag; the next render composes a document-image motion graphic.
            await api.post(`/modules/${moduleId}/fix-visuals`, { scene_ids: [scene_id] });
            toast.success("Visual fixed — it will be used on the next render");
            if (onRetried) onRetried(scene_id);
            // Refresh after a short delay so the audit sees the new render_state.
            setTimeout(() => { refresh(); }, 2500);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRetrying((s) => ({ ...s, [scene_id]: false }));
        }
    }, [moduleId, onRetried, refresh]);

    if (!audit) return null;

    const driftScenes = (audit.scenes || []).filter(
        (s) => (s.drift_reasons || []).some((r) => r === "SCRIPT_DRIFT"),
    );
    const fallbackScenes = (audit.scenes || []).filter(
        (s) => s.image_fallback_used || s.visual_fallback_reason
            || (s.drift_reasons || []).some((r) => r === "APPROVED_VISUAL_NOT_USED"
                || (typeof r === "string" && (r.startsWith("IMAGE_FALLBACK") || r.startsWith("PARTIAL_BUNDLE") || r.startsWith("LAYOUT_DRIFT"))))
    );
    const reapprovalScenes = (audit.scenes || []).filter(
        (s) => s.approval_state === "NEEDS_REAPPROVAL",
    );
    const avDriftScenes = (audit.scenes || []).filter(
        (s) => (s.drift_reasons || []).some(
            (r) => typeof r === "string" && r.startsWith("AV_SYNC_DRIFT"),
        ),
    );

    const hasDrift = driftScenes.length > 0;
    const hasFallback = fallbackScenes.length > 0 || avDriftScenes.length > 0;
    const hasReapproval = reapprovalScenes.length > 0;

    // Phase 5.2 — module-level invariants (duration floor + SOP image
    // coverage + lip-sync). Any failing invariant becomes a red/yellow
    // chip surfaced alongside the per-scene banners. Chips are visible
    // even on a "clean" module so reviewers see the health status at
    // a glance.
    // One duration source: the project status line's value when given, else the audit's plan.
    const curS = currentDurationS ?? audit.planned_duration_s ?? audit.estimated_duration_s ?? 0;
    const estMin = fmtMinutes(curS);
    const targetMin = Number(audit.target_video_minutes || 5).toFixed(1);
    const targetS = Number(audit.target_video_minutes || 5) * 60;
    // Red only when more than 10% away from the target.
    const meetsDurFloor = !(curS > 0 && Math.abs(curS - targetS) / targetS > 0.10);
    const coverage = typeof audit.sop_image_coverage_ratio === "number"
        ? audit.sop_image_coverage_ratio
        : null;
    const coverageOk = coverage === null || coverage >= 0.9;
    const lipSyncFailScenes = (audit.scenes || []).filter(
        (s) => s.lip_sync_ok === false,
    );
    const lipSyncOk = lipSyncFailScenes.length === 0;
    const hasInvariantIssue = !meetsDurFloor || !coverageOk || !lipSyncOk;

    const InvariantChips = () => (
        <div data-testid="fidelity-invariant-chips" className="flex flex-wrap items-center gap-1.5">
            <span
                data-testid="chip-duration"
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                    meetsDurFloor
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-red-200 bg-red-50 text-red-800"
                }`}
                title="Target length ±10%"
            >
                Target {targetMin} min / Current {estMin} min
            </span>
            {coverage !== null && (
                <span
                    data-testid="chip-coverage"
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                        coverageOk
                            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                            : "border-amber-300 bg-amber-50 text-amber-900"
                    }`}
                    title={`${Math.round(coverage * 100)}% of source images used`}
                >
                    SOP images {Math.round(coverage * 100)}%
                </span>
            )}
            <span
                data-testid="chip-lipsync"
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
                    lipSyncOk
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-red-200 bg-red-50 text-red-800"
                }`}
                title={lipSyncOk ? "Lip sync within 1 frame" : `Lip sync failed on ${lipSyncFailScenes.length} scene(s)`}
            >
                Lip sync {lipSyncOk ? "ok" : `${lipSyncFailScenes.length} fail`}
            </span>
        </div>
    );

    // Clean state — green "Storyboard Locked" pill + invariant chips.
    if (!hasDrift && !hasFallback && !hasReapproval && !hasInvariantIssue && audit.ok) {
        return (
            <div className="flex items-center gap-2 flex-wrap">
                <div
                    data-testid="fidelity-locked-pill"
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-800"
                    title="Every scene's approved script and visual are honored in the current render."
                >
                    <ShieldCheck className="h-3 w-3" aria-hidden />
                    Approved (edit to change)
                </div>
                <InvariantChips />
            </div>
        );
    }

    const numberFor = (s) => s.scene_number || s.scene_id?.slice(0, 6);

    return (
        <div className="space-y-3">
            <InvariantChips />
            {!meetsDurFloor && (
                <div
                    data-testid="fidelity-banner-duration"
                    role="alert"
                    className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-sm text-red-900"
                >
                    <div className="flex items-start gap-3">
                        <AlertOctagon className="h-5 w-5 mt-0.5 shrink-0 text-red-700" aria-hidden />
                        <div className="flex-1">
                            <div className="font-semibold text-red-900">
                                Video length is off target
                            </div>
                            <div className="text-red-800 mt-1">
                                Target {targetMin} min / Current {estMin} min. The final render
                                condenses narration to stay within ±10% of the target.
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!lipSyncOk && (
                <div
                    data-testid="fidelity-banner-lipsync"
                    role="alert"
                    className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-sm text-red-900"
                >
                    <div className="flex items-start gap-3">
                        <AlertOctagon className="h-5 w-5 mt-0.5 shrink-0 text-red-700" aria-hidden />
                        <div className="flex-1">
                            <div className="font-semibold text-red-900">
                                Lip sync failed on {lipSyncFailScenes.length} trainer scene{lipSyncFailScenes.length === 1 ? "" : "s"}
                            </div>
                            <div className="text-red-800 mt-1">
                                The trainer's lips drift from the voiceover by more than one frame.
                                Regenerate the affected scenes so the AI re-aligns the audio
                                and avatar video.
                            </div>
                            <div
                                data-testid="fidelity-lipsync-scenes"
                                className="mt-2 flex flex-wrap gap-1.5"
                            >
                                {lipSyncFailScenes.map((s) => (
                                    <span
                                        key={s.scene_id}
                                        className="inline-flex items-center rounded-md bg-white/70 border border-red-300 px-2 py-0.5 font-mono text-[11px] font-semibold text-red-900"
                                        data-testid={`fidelity-lipsync-scene-${numberFor(s)}`}
                                    >
                                        Scene {numberFor(s)} ({s.lip_sync_delta_frames > 0 ? "+" : ""}{s.lip_sync_delta_frames}f)
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {hasReapproval && (
                <div
                    data-testid="fidelity-banner-reapproval"
                    role="alert"
                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                >
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-700" aria-hidden />
                        <div className="flex-1">
                            <div className="font-semibold text-amber-900">
                                {reapprovalScenes.length} scene{reapprovalScenes.length === 1 ? "" : "s"} need re-approval after edits
                            </div>
                            <div className="text-amber-800 mt-1">
                                The final video is blocked until every edited scene is
                                re-approved. Open each scene below and click "Approve
                                Scene" to lock the new content in.
                            </div>
                            <div
                                data-testid="fidelity-banner-reapproval-scenes"
                                className="mt-2 flex flex-wrap gap-1.5"
                            >
                                {reapprovalScenes.map((s) => (
                                    <span
                                        key={s.scene_id}
                                        className="inline-flex items-center rounded-md bg-white/70 border border-amber-300 px-2 py-0.5 font-mono text-[11px] font-semibold text-amber-900"
                                        data-testid={`fidelity-reapproval-scene-${numberFor(s)}`}
                                    >
                                        Scene {numberFor(s)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {hasDrift && (
                <div
                    data-testid="fidelity-banner-drift"
                    role="alert"
                    className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-sm text-red-900"
                >
                    <div className="flex items-start gap-3">
                        <AlertOctagon className="h-5 w-5 mt-0.5 shrink-0 text-red-700" aria-hidden />
                        <div className="flex-1">
                            <div className="font-semibold text-red-900">
                                Script drift on {driftScenes.length} scene{driftScenes.length === 1 ? "" : "s"}
                            </div>
                            <div className="text-red-800 mt-1">
                                The current voiceover text differs from what was approved.
                                To keep the storyboard contract intact, re-approve the scene
                                so the new text becomes the source of truth before this
                                module is rendered.
                            </div>
                            <div
                                data-testid="fidelity-banner-drift-scenes"
                                className="mt-2 flex flex-wrap gap-1.5"
                            >
                                {driftScenes.map((s) => (
                                    <span
                                        key={s.scene_id}
                                        className="inline-flex items-center rounded-md bg-white/70 border border-red-300 px-2 py-0.5 font-mono text-[11px] font-semibold text-red-900"
                                        data-testid={`fidelity-drift-scene-${numberFor(s)}`}
                                    >
                                        Scene {numberFor(s)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {hasFallback && (
                <div
                    data-testid="fidelity-banner-fallback"
                    role="alert"
                    className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                >
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-700" aria-hidden />
                        <div className="flex-1">
                            <div className="font-semibold text-amber-900">
                                {fallbackScenes.length + avDriftScenes.length} scene
                                {(fallbackScenes.length + avDriftScenes.length) === 1 ? "" : "s"}{" "}
                                shipped with a substitute visual
                            </div>
                            <div className="text-amber-800 mt-1">
                                The approved storyboard visual was unavailable, so a
                                substitute was rendered. Retry to regenerate this scene
                                using the approved image, or open the scene to accept
                                the substitute.
                            </div>
                            {fallbackScenes.length > 0 && (
                                <ul
                                    data-testid="fidelity-banner-fallback-scenes"
                                    className="mt-2 space-y-1.5"
                                >
                                    {fallbackScenes.map((s) => (
                                        <li
                                            key={s.scene_id}
                                            className="flex items-center gap-2 flex-wrap"
                                        >
                                            <span
                                                className="inline-flex items-center rounded-md bg-white/70 border border-amber-300 px-2 py-0.5 font-mono text-[11px] font-semibold text-amber-900"
                                                data-testid={`fidelity-fallback-scene-${numberFor(s)}`}
                                            >
                                                Scene {numberFor(s)}
                                            </span>
                                            {s.visual_fallback_reason ? (
                                                <span className="text-[11px] text-amber-700" data-testid={`fidelity-visual-reason-${numberFor(s)}`}>
                                                    ({s.visual_fallback_reason})
                                                </span>
                                            ) : s.image_fallback_reason && (
                                                <span className="text-[11px] text-amber-700">
                                                    ({friendlyReason(s.image_fallback_reason)})
                                                </span>
                                            )}
                                            {canRetry && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    data-testid={`fidelity-retry-${numberFor(s)}`}
                                                    disabled={!!retrying[s.scene_id]}
                                                    onClick={() => retryScene(s.scene_id)}
                                                    className="h-6 px-2 text-[11px] border-amber-300 text-amber-900 hover:bg-amber-100"
                                                >
                                                    <RefreshCw className={`h-3 w-3 mr-1 ${retrying[s.scene_id] ? "animate-spin" : ""}`} />
                                                    Regenerate visual
                                                </Button>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {avDriftScenes.length > 0 && (
                                <div className="mt-2 text-[11px] text-amber-700">
                                    Audio/visual timing off on{" "}
                                    {avDriftScenes.map(numberFor).join(", ")} — a re-render
                                    will re-tighten the sync.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Map internal fallback codes to plain-English text so the banner never
// leaks implementation details to end users.
function friendlyReason(code) {
    if (!code) return "";
    if (code === "current_visual_differs_from_approved") return "current visual differs from what was approved";
    if (code === "trainer_avatar_missing_used_poster_fallback") return "trainer avatar unavailable";
    if (code === "approved_visual_asset_unresolvable") return "approved image could not be loaded";
    return code.replace(/_/g, " ");
}
